import jsPDF from 'jspdf';

export interface PDFOptions {
  title: string;
  subtitle?: string;
  exportType?: string;
  includeLogo?: boolean;
  includeFooter?: boolean;
  includeSummary?: boolean;
  summaryData?: {
    totalRecords?: number;
    exportFormat?: string;
    additionalInfo?: string[];
  };
}

export class PDFUtils {
  /**
   * Creates a standardized PDF with Greencard branding
   */
  static async createStandardizedPDF(options: PDFOptions): Promise<jsPDF> {
    const pdf = new jsPDF();
    
    // Set document properties
    pdf.setProperties({
      title: options.title,
      subject: 'Greencard Sustainability Report',
      author: 'Greencard Sustainability Manager',
      creator: 'Greencard Sustainability Manager'
    });
    
    // Declare variables at function scope
    let yPosition: number;
    let pageNum: number = 1;
    
    // Add logo and header
    if (options.includeLogo !== false) {
      try {
        // Load the logo image
        const logoResponse = await fetch('/greencard-uploads/logo.png');
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(logoBlob);
        });
        
        // Get image dimensions to maintain aspect ratio
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = reject;
          img.src = logoBase64;
        });
        
        // Calculate dimensions maintaining aspect ratio
        const maxWidth = 50; // Maximum width in mm
        const maxHeight = 25; // Maximum height in mm
        const aspectRatio = img.width / img.height;
        
        let logoWidth = maxWidth;
        let logoHeight = maxWidth / aspectRatio;
        
        // If height exceeds max height, scale down proportionally
        if (logoHeight > maxHeight) {
          logoHeight = maxHeight;
          logoWidth = maxHeight * aspectRatio;
        }
        
        // Add logo to PDF (positioned at top left) with proper aspect ratio
        pdf.addImage(logoBase64, 'PNG', 20, 10, logoWidth, logoHeight);
        
        // Add title after logo (adjusted position based on logo width)
        const titleX = 20 + logoWidth + 10; // 10mm gap after logo
        pdf.setFontSize(16);
        pdf.text(options.title, titleX, 25);
        
        if (options.subtitle) {
          pdf.setFontSize(12);
          pdf.text(options.subtitle, titleX, 35);
        }
        
        if (options.exportType) {
          pdf.setFontSize(12);
          pdf.text(`Export Type: ${options.exportType.replace('_', ' ').toUpperCase()}`, titleX, 45);
        }
        
        pdf.setFontSize(12);
        pdf.text(`Generated: ${new Date().toLocaleString()}`, titleX, 55);
        
        yPosition = 70;
        
      } catch (error) {
        console.warn('Could not load logo, using text fallback:', error);
        
        // Fallback to text-based logo
        pdf.setFontSize(20);
        pdf.setTextColor(34, 197, 94); // Green color
        pdf.text('Greencard', 20, 25);
        pdf.setTextColor(0, 0, 0); // Reset to black
        
        // Add subtitle
        pdf.setFontSize(12);
        pdf.text('Sustainability Manager', 20, 35);
        
        // Add title
        pdf.setFontSize(16);
        pdf.text(options.title, 20, 50);
        
        if (options.subtitle) {
          pdf.setFontSize(12);
          pdf.text(options.subtitle, 20, 65);
        }
        
        if (options.exportType) {
          pdf.setFontSize(12);
          pdf.text(`Export Type: ${options.exportType.replace('_', ' ').toUpperCase()}`, 20, 75);
        }
        
        pdf.setFontSize(12);
        pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 85);
        
        yPosition = 100;
      }
    } else {
      // No logo, start with title
      pdf.setFontSize(16);
      pdf.text(options.title, 20, 25);
      
      if (options.subtitle) {
        pdf.setFontSize(12);
        pdf.text(options.subtitle, 20, 35);
      }
      
      if (options.exportType) {
        pdf.setFontSize(12);
        pdf.text(`Export Type: ${options.exportType.replace('_', ' ').toUpperCase()}`, 20, 45);
      }
      
      pdf.setFontSize(12);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 55);
      
      yPosition = 70;
    }
    
    return { pdf, yPosition, pageNum };
  }
  
  /**
   * Adds standardized footer to PDF
   */
  static addFooter(pdf: jsPDF, pageNum: number): void {
    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;
    
    pdf.setFontSize(10);
    pdf.setTextColor(128, 128, 128); // Gray color
    pdf.text(`Page ${pageNum}`, pageWidth - 30, pageHeight - 10);
    pdf.text('Greencard Sustainability Manager', 20, pageHeight - 10);
    pdf.setTextColor(0, 0, 0); // Reset to black
  }
  
  /**
   * Adds summary section to PDF
   */
  static addSummary(pdf: jsPDF, yPosition: number, summaryData?: PDFOptions['summaryData']): number {
    if (!summaryData) return yPosition;
    
    if (yPosition < 200) {
      yPosition += 20;
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Export Summary', 20, yPosition);
      pdf.setFont(undefined, 'normal');
      yPosition += 10;
      
      pdf.setFontSize(10);
      
      if (summaryData.totalRecords !== undefined) {
        pdf.text(`• Total records exported: ${summaryData.totalRecords}`, 25, yPosition);
        yPosition += 7;
      }
      
      if (summaryData.exportFormat) {
        pdf.text(`• Export format: ${summaryData.exportFormat}`, 25, yPosition);
        yPosition += 7;
      }
      
      pdf.text(`• Generated by: Greencard Sustainability Manager`, 25, yPosition);
      yPosition += 7;
      
      if (summaryData.additionalInfo) {
        summaryData.additionalInfo.forEach(info => {
          pdf.text(`• ${info}`, 25, yPosition);
          yPosition += 7;
        });
      }
    }
    
    return yPosition;
  }
  
  /**
   * Handles page breaks and adds footer
   */
  static handlePageBreak(pdf: jsPDF, yPosition: number, pageNum: number, minYPosition: number = 250): { yPosition: number, pageNum: number } {
    if (yPosition > minYPosition) {
      this.addFooter(pdf, pageNum);
      pdf.addPage();
      pageNum++;
      yPosition = 30;
    }
    return { yPosition, pageNum };
  }
  
  /**
   * Creates a complete standardized PDF with content
   */
  static async createCompletePDF(
    options: PDFOptions,
    contentGenerator: (pdf: jsPDF, yPosition: number, pageNum: number) => { yPosition: number, pageNum: number }
  ): Promise<Blob> {
    const { pdf, yPosition: initialYPosition, pageNum: initialPageNum } = await this.createStandardizedPDF(options);
    
    let { yPosition, pageNum } = await contentGenerator(pdf, initialYPosition, initialPageNum);
    
    // Add footer to last page
    if (options.includeFooter !== false) {
      this.addFooter(pdf, pageNum);
    }
    
    // Add summary if requested
    if (options.includeSummary) {
      yPosition = this.addSummary(pdf, yPosition, options.summaryData);
    }
    
    return pdf.output('blob');
  }
} 